import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const { user, getAccessToken } = useAuth();
  const [stats, setStats] = useState(null);
  const [deployments, setDeployments] = useState([]);
  const [failures, setFailures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [resettingLock, setResettingLock] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  const fetchAdminData = async () => {
    const token = getAccessToken();
    if (!token) return;

    try {
      const [statsRes, depsRes, failuresRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_API_URL}/admin/deployments?limit=20`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_API_URL}/admin/failures?limit=20`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
      ]);

      const [statsData, depsData, failuresData] = await Promise.all([
        statsRes.json(),
        depsRes.json(),
        failuresRes.json(),
      ]);

      if (!statsRes.ok) throw new Error(statsData.error || 'Failed to fetch stats');
      if (!depsRes.ok) throw new Error(depsData.error || 'Failed to fetch deployments');
      if (!failuresRes.ok) throw new Error(failuresData.error || 'Failed to fetch failures');

      setStats(statsData);
      setDeployments(depsData.deployments || []);
      setFailures(failuresData.failures || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetTelegramLock = async () => {
    const token = getAccessToken();
    if (!token) return;

    setResettingLock(true);
    setResetSuccess(false);

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/admin/telegram/reset-lock`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();

      if (!res.ok) throw new Error(data.error || 'Failed to reset lock');
      
      setResetSuccess(true);
      setTimeout(() => setResetSuccess(false), 3000);
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setResettingLock(false);
    }
  };

  useEffect(() => {
    fetchAdminData();
  }, []);

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="glass-loader">Loading admin dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-error">
        <h2>Access Denied</h2>
        <p>{error}</p>
        {error.includes('Forbidden') && (
          <p className="admin-hint">Only admin users can access this page.</p>
        )}
      </div>
    );
  }

  const instanceStatusCounts = stats?.instances?.byStatus || {};
  const totalFailed = instanceStatusCounts.failed || 0;
  const totalRunning = (instanceStatusCounts.running || 0) + (instanceStatusCounts.queued || 0);

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div className="admin-header-content">
          <h1>System Admin</h1>
          <p>Internal monitoring dashboard</p>
        </div>
        <button className="btn-refresh" onClick={fetchAdminData}>
          Refresh
        </button>
      </header>

      <nav className="admin-tabs">
        <button
          className={`admin-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`admin-tab ${activeTab === 'deployments' ? 'active' : ''}`}
          onClick={() => setActiveTab('deployments')}
        >
          Deployments
        </button>
        <button
          className={`admin-tab ${activeTab === 'failures' ? 'active' : ''}`}
          onClick={() => setActiveTab('failures')}
        >
          Failures ({totalFailed})
        </button>
      </nav>

      {activeTab === 'overview' && stats && (
        <div className="admin-overview">
          <div className="stat-grid">
            <div className="admin-stat-card">
              <span className="stat-label">Total Deployments</span>
              <span className="stat-value">{stats.deployments?.total || 0}</span>
              <span className="stat-sub">{totalRunning} active</span>
            </div>
            <div className="admin-stat-card">
              <span className="stat-label">Failed Runs</span>
              <span className="stat-value stat-danger">{totalFailed}</span>
              <span className="stat-sub">{instanceStatusCounts.completed || 0} completed</span>
            </div>
            <div className="admin-stat-card">
              <span className="stat-label">Active Users</span>
              <span className="stat-value">{stats.users?.total || 0}</span>
              <span className="stat-sub">{stats.users?.activeSubscriptions || 0} subscribed</span>
            </div>
            <div className="admin-stat-card">
              <span className="stat-label">Credits Used (30d)</span>
              <span className="stat-value">${((stats.usage?.creditsUsed30d || 0) / 100).toFixed(2)}</span>
              <span className="stat-sub">platform cost</span>
            </div>
          </div>

          <div className="overview-row">
            <div className="admin-section">
              <h3>Instance Status Breakdown</h3>
              <div className="status-breakdown">
                {Object.entries(instanceStatusCounts).map(([status, count]) => (
                  <div key={status} className={`status-row status-${status}`}>
                    <span className="status-name">{status}</span>
                    <span className="status-count">{count}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="admin-section">
              <h3>Recent Failures</h3>
              {failures.length === 0 ? (
                <p className="empty-note">No failures in recent logs</p>
              ) : (
                <div className="recent-failures">
                  {failures.slice(0, 5).map(f => (
                    <div key={f.id} className="failure-row">
                      <div className="failure-info">
                        <span className="failure-agent">{f.agentName || f.agentSlug || 'Unknown'}</span>
                        <span className="failure-user">{f.userEmail}</span>
                      </div>
                      <span className="failure-error" title={f.errorMessage}>
                        {f.errorMessage?.substring(0, 60) || 'No message'}
                      </span>
                      <span className="failure-time">
                        {f.failedAt ? new Date(f.failedAt).toLocaleDateString() : 'N/A'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="admin-section admin-system-section">
              <h3>System Management</h3>
              <div className="system-content">
                <div className="system-info">
                  <h4>Telegram System Lock</h4>
                  <p>Manually clear the bot factory lock if a generation gets stuck.</p>
                </div>
                <button 
                  className={`btn-danger-outline ${resetSuccess ? 'success' : ''}`}
                  onClick={handleResetTelegramLock}
                  disabled={resettingLock}
                >
                  {resettingLock ? 'Resetting...' : resetSuccess ? 'Lock Reset ✓' : 'Force Reset Lock'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'deployments' && (
        <div className="admin-deployments">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>User</th>
                <th>Status</th>
                <th>Provider</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {deployments.length === 0 ? (
                <tr><td colSpan="5" className="empty-cell">No deployments found</td></tr>
              ) : (
                deployments.map(d => (
                  <tr key={d.id}>
                    <td>{d.name || 'Unnamed'}</td>
                    <td>{d.profiles?.email || 'Unknown'}</td>
                    <td>
                      <span className={`status-badge status-${d.status}`}>{d.status}</span>
                    </td>
                    <td>{d.provider_id || d.provider}</td>
                    <td>{d.created_at ? new Date(d.created_at).toLocaleString() : 'N/A'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'failures' && (
        <div className="admin-failures">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Agent</th>
                <th>User</th>
                <th>Error</th>
                <th>Failed At</th>
              </tr>
            </thead>
            <tbody>
              {failures.length === 0 ? (
                <tr><td colSpan="4" className="empty-cell">No failures found</td></tr>
              ) : (
                failures.map(f => (
                  <tr key={f.id}>
                    <td>{f.agentName || f.agentSlug || 'Unknown'}</td>
                    <td>{f.userEmail}</td>
                    <td className="error-cell" title={f.errorMessage}>
                      {f.errorMessage || 'No error message'}
                    </td>
                    <td>{f.failedAt ? new Date(f.failedAt).toLocaleString() : 'N/A'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;